/**
 * MCP の接続の許可の画面（/mcp/authorize・pages/McpAuthorize.tsx）の判断（純関数・import なし = node --test でそのまま動く）。
 * 2026-09-28 creator/mcp-dashboard-login（founder 優先度 高）。
 *
 * WHY ダッシュボードで許可するか: MCP のオリジンのログインのフォームでは、ダッシュボードで保存したパスワードが Chrome に出なかった
 *   （founder が Codex でつないだとき）。ダッシュボードなら保存したパスワード・パスキー・Google / GitHub がそのまま使え、
 *   ログイン済みなら同意だけで済む。流れの全体は apps/mcp/src/auth/dashboardLogin.ts の冒頭。
 *
 * ここに移した MCP の同意の画面の決まり（apps/mcp/src/auth/pages.ts・shared.ts）:
 *   - スコープの一覧・危険度の印（writes / money）・最初にチェックするのは読み取りだけ（書き込み・出金は本人が自分でチェック）
 *   - 「このアプリは誰か」の 4 つの書き分け（identityKind・2026-09-28 calm-consent・known_web は 2026-10-04 core/known-web）
 *   - プロジェクト: すべて（後から作るものも）か、選んだものだけ
 *
 * import は launchHidden.ts だけ（それも import なし = pendingAction.ts と同じ形。node --test でそのまま動く）。
 * **URL のパラメータで受けるのは要求 ID（`?request=`）だけ**。名前・戻り先・スコープ・プロジェクトは kds_ で本体から読む
 * （WHY: URL で受けると、攻撃者が名前やスコープを偽った画面を作れる。Core と合意 2026-09-28）。
 */

import { isLaunchHidden } from './launchHidden.ts';

export const MCP_AUTHORIZE_PATH = '/mcp/authorize';

/**
 * MCP のスコープ（apps/mcp/src/scopes.ts の SCOPES と同じ順・同じ危険度。mcpAuthorize.test.ts が突き合わせる）。
 * 文言は i18n の `mcpAuth.scope.<scope の : を _ に>.title / .desc`（scopeMessageKey）。WHY 写しを持つか: ダッシュボードは MCP のコードを import できない（別のアプリ）。
 * 本体が要求と一緒に一覧を返す形にすると、本体に文言を持たせることになる = 画面の言葉はダッシュボードで持つ
 */
export const MCP_SCOPES = [
  { scope: 'read:projects', danger: 'low' },
  { scope: 'read:reports', danger: 'low' },
  { scope: 'read:players', danger: 'low' },
  { scope: 'read:payouts', danger: 'low' },
  { scope: 'write:config', danger: 'medium' },
  { scope: 'write:players', danger: 'medium' },
  { scope: 'deploy', danger: 'medium' },
  { scope: 'read:functions', danger: 'low' },
  { scope: 'read:logs', danger: 'low' },
  { scope: 'write:functions', danger: 'medium' },
  { scope: 'write:mail', danger: 'medium' },
  { scope: 'payouts:request', danger: 'high' }
] as const;

export type McpScope = (typeof MCP_SCOPES)[number]['scope'];
export type McpDanger = 'low' | 'medium' | 'high';

/**
 * スコープの文言のキー（i18n の en.mcpAuth.ts）。スコープの : は _ に替える（辞書の検査 i18n/wording.test.ts のキーの読み方に合わせる）。
 * 初回の公開の言い方（2026-09-29 creator/launch-close-2）: 売上と出金（payouts）を隠している間、read:payouts / read:reports は
 *   `…Launch` のキー（read:payouts =「利用料と前払い残高を見る」・read:reports =「レポートを見る」）。
 *   WHY: スコープ（OAuth の約束 = 名前・中身の権限）は変えず、表示だけを初回用にする。この 2 つで見えるのは、初回では
 *   前払い残高・利用料（prepaid_get・usage_get）と X の投稿からの流入・メールの状況だけ = 「出金」「売上」は閉じた機能の名前。
 *   隠すのをやめたら（launchHidden.ts から payouts を外したら）元のキーに戻る
 */
const LAUNCH_WORDED: ReadonlySet<string> = new Set(['read:payouts', 'read:reports']);
/**
 * メール（mail）を閉じている間の read:reports の説明（2026-09-29 creator/mail-scopes）: 末尾に `NoMail` を足したキー
 *   （descNoMail = 売上の分は残す・descLaunchNoMail = X の投稿からの流入だけ）。WHY: 「メール配信の状況と結果」を読む道具（mail_status 等）も
 *   閉じている（apps/mcp の launchHidden.ts TOOL_FEATURE）= 閉じた機能の名前。title には mail が無いので変えない。
 *   hidden を引数で受けるのは、開けたとき（launchHidden.ts から外したとき）に元のキーへ戻ることをテストで確かめるため
 */
export const scopeMessageKey = (scope: McpScope, part: 'title' | 'desc', hidden: (key: string) => boolean = isLaunchHidden) =>
  `mcpAuth.scope.${scope.replace(':', '_')}.${part}${LAUNCH_WORDED.has(scope) && hidden('payouts') ? 'Launch' : ''}${
    scope === 'read:reports' && part === 'desc' && hidden('mail') ? 'NoMail' : ''
  }`;

/**
 * 最初からチェックする読み取りのセット（本体の DEFAULT_CHECKED_SCOPES と同じ・「指定なし」のときの目安）。
 * 2026-10-04 creator/web-claude-fix: apps/mcp/src/scopes.ts の DEFAULT_SCOPES は「作って公開する」の束（= 下の PRESET_SCOPES.build）に
 * 変わった（Web の Claude が PRM の scopes_supported をそのまま要求するため）。ここは読み取りのまま
 */
export const MCP_DEFAULT_SCOPES: readonly McpScope[] = ['read:projects', 'read:reports', 'read:players', 'read:payouts'];

const SCOPE_SET = new Set<string>(MCP_SCOPES.map((s) => s.scope));
export const isMcpScope = (s: string): s is McpScope => SCOPE_SET.has(s);

/**
 * GET /v1/developers/mcp-authz-requests/:id の応答（型の正: docs/teams/creator/tasks/mcp-grants-api.md ③・Core と合意）。
 * client.name は自己申告（DCR では誰でも「Claude Code」を名乗れる）= 画面では「自称」と分かる書き方で出す
 */
export type McpPresentation = 'verified' | 'loopback_calm' | 'known_web' | 'remote_warning';
/** Web の Claude・ChatGPT（本体が戻り先を公式の一覧と完全一致で確かめたもの・presentation が known_web のときだけ） */
export type KnownWebAppId = 'claude' | 'chatgpt' | 'cursor' | 'muse';
export interface McpAuthRequest {
  id: string;
  status: string;
  expiresAt?: string;
  client: { id: string; name: string; source: 'cimd' | 'dcr'; verifiedDomain: string | null };
  /** 承認後にクライアントへ戻る先のホストと種類（この PC か・インターネット上か） */
  redirect: { host: string; kind: 'loopback' | 'remote' };
  presentation: McpPresentation;
  /** presentation が known_web のときのアプリ（2026-10-04 core/known-web）。それ以外・古い本体は null / 無し */
  knownApp?: KnownWebAppId | null;
  /** 許可できるスコープ（クライアントが求めた既知のもの。指定なしなら全部） */
  allowedScopes: string[];
  /** 最初にチェックするもの（allowed ∩ 読み取り 4 つ） */
  defaultScopes: string[];
  /**
   * 同じ本人・同じアプリに有効な接続があれば、その今の許可（2026-10-05 stepup-ux B・本体 mcpAuthzStepUp.ts）。
   * null / 無し（古い本体）= 初めての接続 = 今までの画面。あれば「Add permission」の画面（mcpAuthorizeStepUp.ts stepUpView）
   */
  existing?: {
    scopes: string[];
    /**
     * その接続が触れるプロジェクト（null = すべて）。2026-10-14 consent-one-button から本体が返す（無い = 古い本体）。
     * 画面が最初から同じプロジェクトを選んでおくため（mcpAuthorizeProjects.ts initialProjects・広げない）
     */
    projectIds?: string[] | null;
  } | null;
  /**
   * 'staging' = 検証用の MCP（kumo-mcp-staging）からの要求（2026-10-08 creator/mcp-staging・本体 lib/mcpStaging.ts）。
   * 画面の一番上に名前「KUMODeck Preview (v0.2)」（辞書の {preview}・2026-10-15 console/preview-name。以前は「{product} 検証用（{product} staging）」）・
   * 「すべてのプロジェクト」は出さない（本体も断る）。無し = 公開の MCP（今までの画面）
   */
  stage?: 'staging';
  /**
   * true = 検証用の MCP が開いた形（本体の KUMO_MCP_STAGING_OPEN=on・2026-10-15 core/staging-open・本体 lib/mcpStaging.ts の冒頭の 3'・4'）。
   * stage: 'staging' のときだけ来る。画面は「先行版」の文にし、「すべてのプロジェクト」も出す（本体がふつうの MCP と同じに受ける）
   */
  stagingOpen?: true;
}
export interface McpAuthRequestResponse {
  request: McpAuthRequest;
  projects: Array<{ id: string; name: string; slug: string }>;
  developer: { email: string; emailVerified: boolean };
}

/** `?request=<ID>` だけを読む。ほかのパラメータ（name・scope 等）は何があっても使わない。形の合わない ID は null */
export function readRequestId(search: string): string | null {
  const id = new URLSearchParams(search).get('request');
  return id && /^[A-Za-z0-9_-]{8,128}$/.test(id) ? id : null;
}

/** 戻り先のホスト（表示用）。URL として読めなければ先頭 80 文字 */
export function redirectHost(uri: string): string {
  try {
    const u = new URL(uri);
    return u.host || u.protocol;
  } catch {
    return uri.slice(0, 80);
  }
}

/** ループバック（手元の PC で動くアプリ。Claude Code・Codex の CLI はこれ）か（apps/mcp/src/auth/pages.ts isLoopbackHost と同じ） */
export function isLoopbackHost(host: string): boolean {
  const h = host.replace(/:\d+$/, '').replace(/^\[|\]$/g, '');
  return h === 'localhost' || h === '127.0.0.1' || h === '::1';
}

/**
 * Web の Claude・ChatGPT の画面の名前と、公式の戻り先のホスト（2026-10-04 core/known-web）。
 * 戻り先の一覧の正本は本体（apps/server/src/modules/platform/mcpKnownWeb.ts・パスまで完全一致で確かめる）。
 * WHY ここでもホストを持つか: 画面には戻り先のホストしか来ない。本体の判定に加えて「ホストがそのアプリの公式のもの」を画面でも確かめる
 *   = 本体・画面のどちらかの不具合で、知らないサーバーに落ち着いた確認が出ないようにする（identityKind の「慎重な方」と同じ考え）。
 *   本体の一覧にホストを足したら、ここも足す（足し忘れは強い警告に倒れるだけ = 安全な側）
 */
export const KNOWN_WEB_APPS: Readonly<Record<KnownWebAppId, { name: string; hosts: readonly string[] }>> = {
  claude: { name: 'Claude', hosts: ['claude.ai', 'claude.com'] },
  chatgpt: { name: 'ChatGPT', hosts: ['chatgpt.com'] },
  // Cursor の Web / Cloud Agents（2026-10-08 creator/cursor-marketplace・戻り先 https://www.cursor.com/agents/mcp/oauth/callback）
  cursor: { name: 'Cursor', hosts: ['www.cursor.com'] },
  // Muse（Meta の AI エージェント・2026-10-25 console/muse-known-app・founder「Muse を公式の戻り先の一覧に足して、『確認されていないアプリ』の
  //   注意を消して」）。戻り先 https://agent.meta.ai/api/hatch/oauth/callback（決まったクライアント kumodeck-muse と同じ・調べは
  //   apps/mcp/src/auth/presetClients.ts）。画面の行は「Access will be sent only to agent.meta.ai, the official address of Muse.」
  muse: { name: 'Muse', hosts: ['agent.meta.ai'] }
};

/**
 * 「このアプリは誰か」の書き分け（MCP の identityBlock と同じ 4 つ・2026-09-28 管理の判断・known_web は 2026-10-04 core/known-web）:
 *   verified  … CIMD で確かめられた。ドメインを出す
 *   loopback  … 未確認で、戻り先がこの PC。**警告の言葉は出さず**「いまつなごうとしましたか？」の落ち着いた確認
 *               （コードはこの PC の中のアプリにしか届かない = 横取りには PC にアプリを入れる必要がある。founder「変に注意が出てくると怖い」）
 *   known_web … 未確認（DCR）だが、戻り先が Web の Claude・ChatGPT の公式の戻り先。落ち着いた確認で「Claude（claude.ai）を連携しますか？」
 *               （コードは公式の戻り先にしか届かない = 偽アプリが Claude を名乗っても受け取れない。founder「Web の Claude・ChatGPT からでもアップロード」）
 *   remote    … 未確認で、戻り先がインターネット上のそれ以外のサーバー。強い警告（フィッシングの本命）
 * 本体の presentation（MCP の判定を本体が同じ規則で確かめたもの）と、画面で受けたホストからの判定の**慎重な方**を使う:
 * どちらかが remote なら remote、verified は両方が verified のとき、known_web は両方が known_web のときだけ（片方だけなら remote）
 */
export type IdentityKind = 'verified' | 'loopback' | 'known_web' | 'remote';
type IdentityReq = Pick<McpAuthRequest, 'presentation' | 'redirect' | 'client'> & { knownApp?: KnownWebAppId | null };

/** 本体の knownApp が一覧のアプリで、戻り先のホストがそのアプリの公式のものか */
function knownWebMatches(req: IdentityReq): boolean {
  const app = req.knownApp;
  if (typeof app !== 'string' || !Object.prototype.hasOwnProperty.call(KNOWN_WEB_APPS, app)) return false;
  return req.redirect.kind === 'remote' && KNOWN_WEB_APPS[app].hosts.includes(req.redirect.host);
}

export function identityKind(req: IdentityReq): IdentityKind {
  const fromServer: IdentityKind =
    req.presentation === 'verified' ? 'verified'
    : req.presentation === 'loopback_calm' ? 'loopback'
    : req.presentation === 'known_web' ? 'known_web'
    : 'remote';
  const local: IdentityKind =
    req.client.verifiedDomain ? 'verified'
    : req.redirect.kind === 'loopback' && isLoopbackHost(req.redirect.host) ? 'loopback'
    : knownWebMatches(req) ? 'known_web'
    : 'remote';
  if (fromServer === 'remote' || local === 'remote') return 'remote';
  if (fromServer === 'known_web' || local === 'known_web') return fromServer === local ? 'known_web' : 'remote';
  if (fromServer === 'verified' && local === 'verified') return 'verified';
  return 'loopback';
}

/** known_web のときの画面の名前（一覧の名前 = 自己申告ではない）。それ以外は null */
export function knownWebAppName(req: IdentityReq): string | null {
  return identityKind(req) === 'known_web' && req.knownApp ? KNOWN_WEB_APPS[req.knownApp].name : null;
}

/** 画面の名前（自己申告・長すぎる名前は切る・空なら null = 「AI エージェント」と書く） */
export function displayName(name: unknown): string | null {
  const s = typeof name === 'string' ? name.trim().slice(0, 80) : '';
  return s || null;
}

/**
 * 初回の公開で選ばせないスコープ（2026-09-29 creator/launch-close-2・定義順）。payouts:request（出金の申請）は、売上と出金（payouts）を隠している間は
 * 「自分で選ぶ」の欄にも出さない。WHY: この権限で使える道具（payout_request・withdrawal_status）が MCP に無い（apps/mcp の launchHidden.ts）
 *   = 選んでも何もできないのに、赤い「お金」の印つきで「出金」を見せることになる。スコープそのもの（OAuth の約束・本体の許可表）は変えない
 *   = 求めてきたエージェントがいても、ここで選べないだけ（承認の本文にも入らない = approvalBody は visibleScopes を通す）
 */
export const launchHiddenScopes = (hidden: (key: string) => boolean = isLaunchHidden): readonly McpScope[] => [
  // write:mail（2026-09-29 creator/mail-scopes）: メール（mail）を閉じている間は、送る道具（mail_send 等）が MCP に無い・本体も
  //   403 feature_unavailable = payouts:request と同じ理由で選ばせない。開けたら（launchHidden.ts から mail を外したら）今までどおり出る
  ...(hidden('mail') ? (['write:mail'] as const) : []),
  ...(hidden('payouts') ? (['payouts:request'] as const) : [])
];

/** 画面に出すスコープ（許可できるものだけ・定義順・初回の公開で選ばせないものを除く）。本体が空で返したら全部（「指定なし」と同じ扱い） */
export function visibleScopes(allowed: readonly string[], isHidden: (key: string) => boolean = isLaunchHidden): Array<(typeof MCP_SCOPES)[number]> {
  const set = new Set(allowed);
  const hidden = new Set<string>(launchHiddenScopes(isHidden));
  return MCP_SCOPES.filter((s) => !hidden.has(s.scope) && (set.size === 0 || set.has(s.scope)));
}

/**
 * 最初のチェック: 本体の defaultScopes のうち、読み取りのセットに入り、許可できるものだけ。
 * 書き込み・出金は、本体が何を返しても最初からチェックしない（MCP の shared.ts consent と同じ決まり = うっかり全権を渡さない）
 */
export function initialScopes(defaults: readonly string[], allowed: readonly string[]): McpScope[] {
  const d = new Set(defaults);
  const visible = new Set<string>(visibleScopes(allowed).map((s) => s.scope));
  return MCP_DEFAULT_SCOPES.filter((s) => d.has(s) && visible.has(s));
}

/**
 * 選び方（2026-09-28 管理の判断・founder がチェックの欄 12 個を見て「とりあえず全部許可」した）: 上に 3 つ置き、欄は「自分で選ぶ」に畳む。
 *   build  … 作って公開する（おすすめ・既定）= 見る系すべて + 設定を変える + 公開とデプロイ + サーバー関数を管理する（ログを読むは見る系）
 *   read   … 見るだけ = 見る系すべて
 *   custom … 自分で選ぶ = 今の欄（最初は直前の選び方のチェックのまま）
 * **お金（出金の申請）・メールの送信・BAN（write:players）は、どの選び方にも入れない**（「自分で選ぶ」でだけ足せる）。
 *   WHY: 取り返しがつかない・利用者に届く・お金が動く = うっかり渡さない。書き込みの確認（本番・削除・送信）は MCP の側でいまのまま効く。
 * 以前の決まり「最初にチェックするのは読み取りだけ」（MCP の shared.ts）は、既定を build にした時点で変わった（管理の判断）。
 * 本体の defaultScopes（読み取り 4 つ）は「指定なし」のときの目安として残るが、画面の既定はこの build
 */
export type ScopePreset = 'build' | 'read' | 'custom';
export const PRESET_ORDER: readonly ScopePreset[] = ['build', 'read', 'custom'];
const READ_ALL: readonly McpScope[] = ['read:projects', 'read:reports', 'read:players', 'read:payouts', 'read:functions', 'read:logs'];
export const PRESET_SCOPES: Record<Exclude<ScopePreset, 'custom'>, readonly McpScope[]> = {
  build: [...READ_ALL, 'write:config', 'deploy', 'write:functions'],
  read: READ_ALL
};
/**
 * 選び方の説明のキー（i18n の `mcpAuth.preset.<p>Note`）。閉じた機能の名前を同意の画面に出さないため、初回の公開では …Launch の文にする:
 * - custom（2026-09-29 creator/mail-scopes）: 「出金の申請・メールの送信・BAN はここでだけ足せる」→ 出金とメールが両方閉じている間は
 *   `customNoteLaunch`（BAN だけ）。WHY: 選べないもの（launchHiddenScopes）を「ここで足せる」と書くと、欄を探しても無い = 迷わせる。
 *   片方だけ開いた形（payouts だけ開けて mail は閉じたまま）は元の文のまま（Core: mail だけ開けるのは起動エラー = 逆は無い）
 * - read（2026-09-30 creator/agt-16）: 「売上・残高を見られる」→ payouts が閉じている間は `readNoteLaunch`（レポート・利用料と前払い残高）。
 *   read:payouts / read:reports の表示を …Launch にしたのと同じ理由（scopeMessageKey）
 * 開けたら元の文に戻る。hidden を引数で受けるのは、それをテストで確かめるため。
 */
export function presetNoteKey(preset: ScopePreset, hidden: (key: string) => boolean = isLaunchHidden): string {
  const launch = (preset === 'custom' && hidden('mail') && hidden('payouts')) || (preset === 'read' && hidden('payouts'));
  return `mcpAuth.preset.${preset}Note${launch ? 'Launch' : ''}`;
}

/** どの選び方にも入れないもの（「自分で選ぶ」でだけ） */
export const NEVER_PRESET: readonly McpScope[] = ['payouts:request', 'write:mail', 'write:players'];

/** 選び方のスコープ（許可できるものとの重なり・定義順）。custom は null（欄で選ぶ） */
export function presetScopes(preset: ScopePreset, allowed: readonly string[]): McpScope[] | null {
  if (preset === 'custom') return null;
  const want = new Set<string>(PRESET_SCOPES[preset]);
  return visibleScopes(allowed)
    .map((s) => s.scope)
    .filter((s) => want.has(s));
}

/** 最初の選び方: build が許可できるものと 1 つでも重なれば build。重ならなければ read、それも無ければ custom（エージェントが書き込みや出金だけを求めた等） */
export function initialPreset(allowed: readonly string[]): ScopePreset {
  if ((presetScopes('build', allowed) ?? []).length > 0) return 'build';
  if ((presetScopes('read', allowed) ?? []).length > 0) return 'read';
  return 'custom';
}

export type ProjectsMode = 'all' | 'selected';

/**
 * 承認の本文を作る（送る前の確かめ）。エラーは i18n のキーの末尾（`mcpAuth.error.<code>`）。
 * 自分のものでないプロジェクト ID（改ざん）は捨てる（本体も断るが、送らない）
 */
export function approvalBody(
  scopes: readonly string[],
  mode: ProjectsMode,
  selected: readonly string[],
  ownedIds: readonly string[],
  allowed: readonly string[] = []
): { ok: true; body: { scopes: McpScope[]; projectIds: string[] | null } } | { ok: false; error: 'noScope' | 'noProject' } {
  const set = new Set(scopes);
  // 許可できないもの（求められていない）は送らない（本体は 400 scope_not_requested で断る）
  const chosen = visibleScopes(allowed)
    .map((s) => s.scope)
    .filter((s) => set.has(s));
  if (chosen.length === 0) return { ok: false, error: 'noScope' };
  if (mode === 'all') return { ok: true, body: { scopes: chosen, projectIds: null } };
  const owned = new Set(ownedIds);
  const ids = [...new Set(selected)].filter((id) => owned.has(id));
  if (ids.length === 0) return { ok: false, error: 'noProject' };
  return { ok: true, body: { scopes: chosen, projectIds: ids } };
}

/**
 * 承認 / 拒否の後に移る先（本体が返した URL）。**ダッシュボードは戻り先を組み立てない**（本体が MCP のオリジンの決まったパスを返す）。
 * ここでは http(s) かだけを確かめる（javascript: 等へは移らない）。合わなければ null = 画面にエラーを出す
 */
export function safeNextUrl(url: unknown): string | null {
  if (typeof url !== 'string') return null;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.toString() : null;
  } catch {
    return null;
  }
}
